import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Eye, EyeOff, Lock, ClipboardCheck, Users, Building2, Activity } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

// ScapeSource platform staff sign in here (/admin), never on the public
// /login page — that one only offers the Contractor and Vendor portals, so
// the admin portal isn't advertised to customers. The backend enforces the
// same split: a staff account can only authenticate with portal="staff".
//
// Styled on the app's design system (index.css / tailwind.config.js):
// Blueprint navy drafting-grid panel (.ss-sidebar, same as the portal
// sidebar), safety-orange actions, warm paper ground, Source Serif
// headings, IBM Plex Mono for system text.

const RESPONSIBILITIES = [
  { icon: ClipboardCheck, label: "Onboarding queue", detail: "Approve vendor and contractor sign-ups" },
  { icon: Users, label: "Vendor leads", detail: "Grow the in-network vendor base" },
  { icon: Building2, label: "Directories", detail: "Every company on the platform" },
  { icon: Activity, label: "Transaction monitor", detail: "Live purchasing across tenants" },
];

function BrandMark({ subtitle }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-white font-extrabold text-[15px] tracking-tight">
        <span className="w-6 h-6 rounded-md bg-[#E8792E] flex items-center justify-center text-white text-[10px] font-extrabold">SS</span>
        ScapeSource
      </div>
      <div className="text-[10.5px] uppercase tracking-wider text-[#7EA4C4] font-semibold mt-1">{subtitle}</div>
    </div>
  );
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  if (user && user.portal === "staff") return <Navigate to="/staff" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, "staff");
      navigate("/staff");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full h-11 px-3.5 rounded-md border border-slate-200 bg-white text-sm text-slate-900 outline-none " +
    "transition-shadow focus:border-sky-400 focus:ring-2 focus:ring-sky-100";

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="admin-login-page">
      {/* Blueprint panel — desktop only */}
      <aside className="hidden lg:flex ss-sidebar w-[44%] max-w-[560px] flex-col justify-between px-12 py-10 relative">
        <div className="absolute inset-y-0 right-0 w-[3px] bg-[#E8792E]" aria-hidden="true" />
        <BrandMark subtitle="Platform Admin" />

        <div>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#F0A76F] border border-[#E8792E]/40 rounded px-2 py-1">
            <Lock size={11} /> Restricted access
          </span>
          <h2 className="text-white text-[34px] leading-[1.15] font-semibold mt-5">
            The control room for every purchase on ScapeSource.
          </h2>
          <ul className="mt-9 space-y-4">
            {RESPONSIBILITIES.map(({ icon: Icon, label, detail }) => (
              <li key={label} className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-md bg-white/[0.08] border border-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={15} className="text-[#A9C2D8]" />
                </span>
                <div>
                  <div className="text-[13.5px] font-semibold text-white">{label}</div>
                  <div className="text-[12.5px] text-[#7EA4C4]">{detail}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mono text-[10.5px] tracking-wider text-[#5C7E9C] uppercase">
          ScapeSource · Admin console
        </div>
      </aside>

      {/* Sign-in */}
      <main className="flex-1 flex flex-col">
        {/* Navy header strip on small screens, where the panel is hidden */}
        <div className="lg:hidden ss-sidebar px-6 py-5 border-b-[3px] border-[#E8792E]">
          <BrandMark subtitle="Platform Admin" />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-[400px]">
            <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">Staff only</div>
            <h1 className="text-[30px] font-semibold text-slate-900 mt-1.5">Sign in</h1>
            <p className="text-slate-500 text-sm mt-1.5 mb-7">
              Use your ScapeSource staff account. Contractors and vendors sign in at{" "}
              <Link to="/login" className="text-sky-600 font-medium underline decoration-sky-200 underline-offset-2 hover:decoration-sky-600">
                the main login
              </Link>.
            </p>

            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 shadow-[0_1px_2px_rgba(32,36,31,0.04)]">
              <div>
                <label htmlFor="admin-email" className="text-[13px] font-medium text-slate-700">Staff email</label>
                <input
                  id="admin-email" type="email" required autoComplete="username" autoFocus
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@scapesource.com"
                  data-testid="admin-login-email-input"
                  className={`mt-1.5 ${inputClass}`}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="admin-password" className="text-[13px] font-medium text-slate-700">Password</label>
                  <Link to="/forgot-password" className="text-[12px] text-sky-600 hover:text-sky-700">Forgot password?</Link>
                </div>
                <div className="relative mt-1.5">
                  <input
                    id="admin-password" type={showPassword ? "text" : "password"} required autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-login-password-input"
                    className={`${inputClass} pr-10`}
                  />
                  <button
                    type="button" onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-0 top-0 h-11 w-10 flex items-center justify-center text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-[12.5px] text-[#9c3a25] bg-[#f6e2dd] rounded-md px-3 py-2" data-testid="admin-login-error">
                  {error}
                </div>
              )}

              <button
                type="submit" disabled={loading} data-testid="admin-login-submit-button"
                className="w-full h-11 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-[14px] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <Lock size={14} />
                {loading ? "Signing in…" : "Sign in to admin console"}
              </button>
            </form>

            <p className="mono text-[10.5px] text-slate-400 mt-6 leading-relaxed">
              Access is limited to authorized ScapeSource staff. Repeated failed attempts are locked out for 15 minutes.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
